import React, { ReactNode, useEffect, useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { tasksWord } from '../../utils/plural';
export type DayPartTiming = 'past' | 'now' | 'future';
interface DayPartProps {
  title: string;
  timing: DayPartTiming;
  total: number;
  done: number;
  /** Kratek kontekst v glavi (npr. večerna pravila). */
  note?: string;
  /** Povzetek v zaprtem stanju, kadar ni števila nalog (npr. »Ta teden«). */
  summary?: string;
  children: ReactNode;
}
export function DayPart({
  title,
  timing,
  total,
  done,
  note,
  summary,
  children
}: DayPartProps) {
  const allDone = total > 0 && done === total;
  const [open, setOpen] = useState(timing === 'now' && !allDone);
  const reduce = useReducedMotion();
  const bodyId = useId();

  // Ko so vse naloge trenutnega dela opravljene, se del zapre s potrditvijo.
  useEffect(() => {
    if (timing === 'now' && allDone) setOpen(false);
  }, [timing, allDone]);
  const left = total - done;
  let status: ReactNode;
  if (allDone) {
    status = <span className="inline-flex items-center gap-1 text-done-ink" data-id="element-1152" data-mp="357a3256hxeqf">
        opravljeno <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-1153" data-mp="313lvvnls0l5p" />
      </span>;
  } else if (timing === 'past' && left > 0) {
    status = <span className="text-review-ink" data-id="element-1154" data-mp="2w7en7n1zwvk2">še {left} od {total}</span>;
  } else if (summary) {
    status = <span className="text-muted" data-id="element-1155" data-mp="2r8admvb5svt7">{summary}</span>;
  } else if (total > 0) {
    status = <span className="text-muted" data-id="element-1156" data-mp="2mdxpi7yqmwb4">{timing === 'now' ? `${done}/${total}` : tasksWord(total)}</span>;
  } else {
    status = <span className="text-muted" data-id="element-1157" data-mp="2hebc9yiwtg2b">nič obveznega</span>;
  }
  return <section className={`rounded-3xl bg-surface ${timing === 'now' ? 'border-2 border-accent shadow-card' : 'border-[1.5px] border-line'}`} data-id="element-1158" data-mp="2dg40zj77t6yi">
      <h2 data-id="element-1159" data-mp="23gmtcw8ekizw">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={bodyId} className="flex w-full items-center gap-3 rounded-3xl px-4 py-3.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-1160" data-mp="23gwmutsnp8ac">
          <span className="min-w-0 flex-1" data-id="element-1161" data-mp="1jvmjmijy1tar">
            <span className="flex flex-wrap items-center gap-2" data-id="element-1162" data-mp="1jwxroukf9n7o">
              <span className={`font-display text-xl ${timing === 'now' ? 'text-accent-ink' : ''}`} data-id="element-1163" data-mp="1ouph9hxlgx8p">{title}</span>
              {timing === 'now' && <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-extrabold text-white" data-id="element-1164" data-mp="2cllewvoqemnv">zdaj</span>}
            </span>
            {note && <span className="mt-0.5 block text-xs font-semibold text-info-ink" data-id="element-1165" data-mp="22u8lhznabofq">{note}</span>}
          </span>
          <span className="shrink-0 text-sm font-extrabold tabular-nums" data-id="element-1166" data-mp="27s0ih82se0qs">{status}</span>
          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-muted transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1167" data-mp="2vhuqncc5v14p" />
        </button>
      </h2>
      <AnimatePresence initial={false} data-id="element-1168" data-mp="ot2kyk5aiwzc">
        {open && <motion.div id={bodyId} initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-1169" data-mp="jvaqpjh6ayrz">
            <div className="border-t border-line pb-2" data-id="element-1170" data-mp="w58ur7qspbm">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}